import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Package, ChevronLeft, ChevronRight, Plane } from 'lucide-react';
import Layout from '@/components/Layout';
import { FLEET_ITEMS, ROUTE_PATHS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

const EXTRA_IMGS: Record<string, string> = {
  '737max': '/images/B 737 - MAX 9_xIRuSZ.png',
  '737max9': '/images/November 25x21.5cm (Max 9)_ydknHi.jpg',
  'cabin-crew': '/images/57x42cm(Cabin Crew).jpg',
  'experience': '/images/57x42cm(Experience).jpg',
  'pilot': '/images/57x42cm(Pilot).jpg',
  'cargo-freighter': '/images/January 25x21.5cm (Cargo Freighter)_ydknHi.jpg',
};

export default function Fleet() {
  const [selected, setSelected] = useState(0);
  const fleet = FLEET_ITEMS;

  const prev = () => setSelected(i => (i - 1 + fleet.length) % fleet.length);
  const next = () => setSelected(i => (i + 1) % fleet.length);
  const current = fleet[selected];

  return (
    <Layout>
      {/* Hero: large aircraft photo + overlay text */}
      <section className="relative h-[55vh] min-h-[400px] overflow-hidden">
        <img src="/images/57x42cm(Dreamliner).jpg" alt="机队" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-secondary/90" />
        <div className="absolute inset-0 flex flex-col justify-end pb-12">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <h1 className="text-5xl font-bold text-white mb-1">机队介绍</h1>
            <p className="text-white/65 text-sm">16架现代化机型 · 波音 · 空客 · 庞巴迪</p>
          </div>
        </div>
      </section>

      {/* Fleet carousel — full‑width */}
      <section className="bg-secondary text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <p className="text-white/50 text-sm">选择机型了解详情</p>
            <div className="flex gap-2">
              <button onClick={prev} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition">
                <ChevronLeft size={16} />
              </button>
              <button onClick={next} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="flex gap-3 overflow-x-auto pb-4 mb-8">
            {fleet.map((f, i) => (
              <button key={f.id} onClick={() => setSelected(i)}
                className={`shrink-0 w-36 rounded-xl overflow-hidden border-2 transition-all ${
                  i === selected ? 'border-accent shadow-xl scale-105' : 'border-transparent opacity-60 hover:opacity-90'
                }`}>
                <img src={f.image} alt={f.model} className="w-full h-20 object-cover" />
                <div className={`py-1.5 px-2 text-xs font-medium text-center ${i === selected ? 'bg-accent/20 text-accent' : 'bg-white/5 text-white/60'}`}>
                  {f.model.split(' ').slice(-2).join(' ')}
                </div>
              </button>
            ))}
          </div>

          {/* Detail */}
          <AnimatePresence mode="wait">
            <motion.div key={current.id}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={springPresets.gentle}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Aircraft image */}
              <div className={`relative rounded-2xl overflow-hidden h-64 lg:h-80 ${(current.id === 'b767f' || current.id === 'q400') ? 'bg-secondary/80' : ''}`}>
                <img
                  src={current.image}
                  alt={current.model}
                  className={`w-full h-full transition-transform duration-500 ${
                    (current.id === 'b767f' || current.id === 'q400')
                      ? 'object-contain object-center p-6'
                      : 'object-cover'
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/40 to-transparent" />
                {current.id === '737max' && (
                  <img src={EXTRA_IMGS['737max']} alt="737 MAX livery"
                    className="absolute bottom-3 right-3 w-24 h-10 object-contain drop-shadow-lg" />
                )}
              </div>

              {/* Specs */}
              <div>
                <p className="text-accent text-xs uppercase tracking-widest font-bold mb-1">{current.model.split(' ').slice(0,2).join(' ')}</p>
                <h2 className="text-4xl font-bold text-white mb-2">{current.model}</h2>
                <p className="text-white/60 text-sm mb-5 leading-relaxed">{current.description}</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {[
                    { icon: <Users size={15} />, label: '公务舱', v: current.businessClass > 0 ? current.businessClass + ' 席' : '—' },
                    { icon: <Users size={15} />, label: '经济舱', v: current.economyClass > 0 ? current.economyClass + ' 席' : '—' },
                    { icon: <Plane size={15} />, label: '架次', v: current.count + ' 架' },
                    { icon: <Package size={15} />, label: '航线', v: '见下方' },
                  ].map(s => (
                    <div key={s.label} className="bg-white/8 rounded-xl p-3">
                      <div className="text-white/50 flex items-center gap-1 text-xs mb-1">{s.icon} {s.label}</div>
                      <div className="text-white font-bold text-sm">{s.v}</div>
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <p className="text-white/50 text-xs font-medium mb-2">主要执飞航线</p>
                  <p className="text-white/80 text-sm bg-white/10 rounded-xl px-3 py-2">{current.routes}</p>
                </div>

                <div>
                  <p className="text-white/50 text-xs font-medium mb-2">机型特色</p>
                  <div className="flex flex-wrap gap-2">
                    {current.features.map(f => (
                      <span key={f} className="px-2.5 py-1 bg-accent/15 text-accent text-xs rounded-full border border-accent/30">{f}</span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dot nav */}
          <div className="flex justify-center gap-2 mt-8">
            {fleet.map((_, i) => (
              <button key={i} onClick={() => setSelected(i)}
                className={`rounded-full transition-all duration-300 ${i === selected ? 'w-6 h-2 bg-accent' : 'w-2 h-2 bg-white/30'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Cabin experience photos */}
      <section className="py-14 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-1">客舱体验</h2>
          <p className="text-muted-foreground text-sm mb-8">从服务到餐饮，让每次飞行都是享受</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { img: '/images/experience-crew-crop.jpg', label: '餐饮服务', pos: 'object-[center_20%]' },
              { img: '/images/25x21.5cm (Twiga Lounge)_gYUbNj.jpg', label: 'Twiga Lounge', pos: 'object-center' },
              { img: '/images/25x21.5cm (Wine).jpg', label: '机上佳酿', pos: 'object-[center_20%]' },
              { img: '/images/57x42cm(Cabin Crew).jpg', label: '专业空乘', pos: 'object-[center_15%]' },
              { img: '/images/57x42cm(Pilot).jpg', label: '机长团队', pos: 'object-[center_25%]' },
              { img: '/images/fun-photo-crop.jpg', label: '温馨服务', pos: 'object-[center_10%]' },
            ].map(item => (
              <div key={item.label} className="relative rounded-2xl overflow-hidden h-56 group">
                <img src={item.img} alt={item.label}
                  className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${item.pos || ''}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white text-sm font-semibold">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-10 bg-secondary text-white text-center">
        <h2 className="text-2xl font-bold mb-1">体验梦想客机的舒适之旅</h2>
        <p className="text-white/55 text-sm mb-5">广州每周三班直飞 · B787-8梦想客机全程执飞</p>
        <Link to={ROUTE_PATHS.BOOKING}
          className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-accent-foreground rounded-xl font-bold hover:bg-accent/90 transition">
          立即订票 <Plane size={16} />
        </Link>
      </section>
    </Layout>
  );
}
